import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import StructuredData from '../components/StructuredData';
import {
  PageContainer,
  Container,
  PageTitle,
  ContentText,
  GlassCard,
  FormField,
  FormLabel,
  FormInput,
  FormSelect,
  FormTextArea,
  FormButton,
  StatusMessage,
  fadeInUp,
  fadeInLeft,
  fadeInRight,
  heroStaggerContainerVariants,
  heroStaggerItemVariants,
  ItemCardLight,
  SectionDivider,
} from '../components/shared/SharedComponents';
import { useContent } from '../hooks/useContent';
import { useNavigationAwareAnimations } from '../hooks/useNavigationAwareAnimations';
import { SHOW_NEW_PAGES_IN_NAV } from '../config/features';

const ContentStack = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 40px;
  width: 100%;
  margin-top: 60px;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const FormSection = styled(GlassCard).attrs({ className: 'large-padding' })`
  width: 100%;
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;


const LeftAlignedSectionSubtitle = styled(motion.h2)`
  font-weight: 700;
  margin-bottom: 20px;
  text-align: left;
  font-size: 22px;
  color: #1d334e;
`;

const HeroSection = styled(motion.section)`
  margin-bottom: 80px;
  
  @media (max-width: 768px) {
    margin-bottom: 60px;
  }
`;

const FormIntroTitle = styled(motion.h2)`
  font-size: clamp(20px, 3vw, 28px);
  font-weight: 700;
  color: #1d334e;
  margin-bottom: 16px;
  letter-spacing: -0.02em;
`;

const FormIntroSubtitle = styled(motion.h3)`
  font-size: clamp(16px, 2.5vw, 20px);
  font-weight: 600;
  color: #1d334e;
  margin-top: 16px;
  margin-bottom: 12px;
`;

const FormIntroText = styled(motion.p)`
  font-size: clamp(16px, 2.5vw, 16px);
  color: rgba(29, 51, 78, 0.8);
  line-height: 1.5;
  font-weight: 400;
  margin-bottom: 24px;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  align-self: start;
`;

const ContactInfoCard = styled(GlassCard).attrs({ className: 'medium-padding' })`
  display: flex;
  flex-direction: column;
  height: fit-content;
  width: 100%;
`;

const ContactInfoTitle = styled(motion.h3)`
  font-size: clamp(20px, 3vw, 20px);
  font-weight: 700;
  color: #1d334e;
  margin-bottom: 12px;
  letter-spacing: -0.02em;
`;

const ContactInfoItem = styled(motion.div)`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ContactInfoLabel = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: rgba(29, 51, 78, 0.6);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ContactInfoValue = styled.p`
  font-size: clamp(16px, 2.5vw, 16px);
  color: #1d334e;
  line-height: 1.6;
  font-weight: 400;
`;

const ContactInfoLink = styled.a`
  color: #1d334e;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ContactInfoFooterNote = styled.p`
  font-size: 1rem;
  font-style: italic;
  color: rgba(29, 51, 78, 0.75);
  line-height: 1.6;
  margin-top: 24px;
  margin-bottom: 0;
`;

const ContextCardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin: 20px 0;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormCheckboxWrapper = styled(FormField)`
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
`;

const FormCheckbox = styled.input.attrs({ type: 'checkbox' })`
  width: 20px;
  height: 20px;
  margin-top: 2px;
  accent-color: #bf1922;
  cursor: pointer;
`;

const FormCheckboxLabel = styled.label`
  font-size: 14px;
  color: #1d334e;
  line-height: 1.5;
  cursor: pointer;
  user-select: none;
`;

// StatusMessage is now imported from SharedComponents

const Contact = () => {
  useNavigationAwareAnimations(); // This handles scroll restoration automatically
  const { contact, contactInfo, footerInfo, site } = useContent();

  const [formData, setFormData] = useState({
    fullName: '',
    organization: '',
    roleTitle: '',
    businessEmail: '',
    directContactNumber: '',
    country: '',
    engagementType: '',
    sector: '',
    timeframe: '',
    message: '',
    acknowledge: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });

    if (!formData.acknowledge) {
      setStatus({
        type: 'error',
        message: 'Please acknowledge that this submission is confidential and does not constitute a binding commitment by either party before sending.'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        // If response is not valid JSON
        setStatus({
          type: 'error',
          message: response.ok
            ? 'Invalid response from server. Please try again.'
            : `Server error: ${response.status} ${response.statusText || 'Unknown error'}`
        });
        return;
      }

      if (response.ok && result.success) {
        setStatus({
          type: 'success',
          message: 'Thank you for your message! We\'ll get back to you soon.'
        });
        setFormData({
          fullName: '', organization: '', roleTitle: '', businessEmail: '', directContactNumber: '', country: '',
          engagementType: '', sector: '', timeframe: '', message: '', acknowledge: false
        });
      } else {
        setStatus({
          type: 'error',
          message: result.message || 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setStatus({
        type: 'error',
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title={`${contact.title || 'Contact Us'} – APEXGATE GLOBAL`}
        description={contact.description || contact.firstSection.paragraphs[0]}
      />
      <StructuredData
        type="WebPage"
        data={{
          page: {
            title: `${contact.title || 'Contact Us'} – APEXGATE GLOBAL`,
            description: contact.description || contact.firstSection.paragraphs[0],
            url: '/contact'
          },
          organization: { footerInfo, contactInfo },
          site
        }}
        includeOrganization={true}
      />
      <PageContainer>
        <Container>
          <article>
            {/* Page Title */}
            <HeroSection>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={heroStaggerContainerVariants}
              >
                <PageTitle
                  className="with-underline"
                  variants={heroStaggerItemVariants}
                >
                  {contact.title}
                </PageTitle>
              </motion.div>
            </HeroSection>

            {/* First Section - Main Content */}
            <HeroSection {...fadeInUp}>
              <LeftAlignedSectionSubtitle>{contact.firstSection.title}</LeftAlignedSectionSubtitle>
              <ContentText>
                <p>{contact.firstSection.paragraphs[0]}</p>
                <p>{contact.firstSection.paragraphs[1]}</p>
              </ContentText>
              <ContextCardsGrid>
                {contact.firstSection.bulletPoints.map((point, index) => (
                  <ItemCardLight key={index}>{point}</ItemCardLight>
                ))}
              </ContextCardsGrid>
              <ContentText>
                <p>{contact.firstSection.paragraphs[2]}</p>
              </ContentText>
            </HeroSection>
          </article>

          <SectionDivider />

          <ContentStack>
            <FormSection {...fadeInLeft}>
              <FormIntroTitle>{contact.secondSection.heading}</FormIntroTitle>
              {contact.secondSection.description && (
                <FormIntroText>{contact.secondSection.description}</FormIntroText>
              )}
              <FormIntroSubtitle>{contact.secondSection.subtitle}</FormIntroSubtitle>
              <FormIntroText>
                {contact.secondSection.paragraph}
              </FormIntroText>

              <ContactForm onSubmit={handleSubmit}>
                <FormField>
                  <FormLabel htmlFor="fullName">{contact.form.fields.fullName}</FormLabel>
                  <FormInput
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder={contact.form.placeholder.fullName}
                    required
                  />
                </FormField>

                <FormField>
                  <FormLabel htmlFor="organization">{contact.form.fields.organization}</FormLabel>
                  <FormInput
                    type="text"
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    placeholder={contact.form.placeholder.organization}
                    required
                  />
                </FormField>

                <FormField>
                  <FormLabel htmlFor="roleTitle">{contact.form.fields.roleTitle}</FormLabel>
                  <FormInput
                    type="text"
                    id="roleTitle"
                    name="roleTitle"
                    value={formData.roleTitle}
                    onChange={handleChange}
                    placeholder={contact.form.placeholder.roleTitle}
                  />
                </FormField>

                <FormField>
                  <FormLabel htmlFor="businessEmail">{contact.form.fields.businessEmail}</FormLabel>
                  <FormInput
                    type="email"
                    id="businessEmail"
                    name="businessEmail"
                    value={formData.businessEmail}
                    onChange={handleChange}
                    placeholder={contact.form.placeholder.businessEmail}
                    required
                  />
                </FormField>

                <FormField>
                  <FormLabel htmlFor="directContactNumber">{contact.form.fields.directContactNumber}</FormLabel>
                  <FormInput
                    type="tel"
                    id="directContactNumber"
                    name="directContactNumber"
                    value={formData.directContactNumber}
                    onChange={handleChange}
                    placeholder={contact.form.placeholder.directContactNumber}
                    required
                  />
                </FormField>

                <FormField>
                  <FormLabel htmlFor="country">{contact.form.fields.country}</FormLabel>
                  <FormInput
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder={contact.form.placeholder.country}
                  />
                </FormField>

                <FormField>
                  <FormLabel htmlFor="engagementType">{contact.form.fields.engagementType}</FormLabel>
                  <FormSelect
                    id="engagementType"
                    name="engagementType"
                    value={formData.engagementType}
                    onChange={handleChange}
                  >
                    {contact.form.engagementTypeOptions.map((option, index) => (
                      <option key={index} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </FormSelect>
                </FormField>

                <FormField>
                  <FormLabel htmlFor="sector">{contact.form.fields.sector}</FormLabel>
                  <FormSelect
                    id="sector"
                    name="sector"
                    value={formData.sector}
                    onChange={handleChange}
                  >
                    {contact.form.sectorOptions
                      .filter(option => SHOW_NEW_PAGES_IN_NAV || !['aerospace', 'marine-offshore'].includes(option.value))
                      .map((option, index) => (
                        <option key={index} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                  </FormSelect>
                </FormField>

                <FormField>
                  <FormLabel htmlFor="message">{contact.form.fields.message}</FormLabel>
                  <FormTextArea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={contact.form.placeholder.message}
                    rows={8}
                    required
                  />
                </FormField>

                <FormField>
                  <FormLabel htmlFor="timeframe">{contact.form.fields.timeframe}</FormLabel>
                  <FormSelect
                    id="timeframe"
                    name="timeframe"
                    value={formData.timeframe}
                    onChange={handleChange}
                  >
                    {contact.form.timeframeOptions.map((option, index) => (
                      <option key={index} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </FormSelect>
                </FormField>

                <FormCheckboxWrapper>
                  <FormCheckbox
                    id="acknowledge"
                    name="acknowledge"
                    checked={formData.acknowledge}
                    onChange={handleChange}
                    required
                  />
                  <FormCheckboxLabel htmlFor="acknowledge">
                    {contact.form.fields.acknowledge}
                  </FormCheckboxLabel>
                </FormCheckboxWrapper>

                <FormButton
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? contact.form.sending : contact.form.button}
                </FormButton>

                {status.message && (
                  <StatusMessage
                    className={status.type}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                  >
                    {status.message}
                  </StatusMessage>
                )}
              </ContactForm>
            </FormSection>

            <RightColumn>
              {contact.businessUnitContacts && (
                <ContactInfoCard {...fadeInRight}>
                  <ContactInfoTitle>{contact.businessUnitContacts.title}</ContactInfoTitle>
                  {contact.businessUnitContacts.units.map((unit) => (
                    <ContactInfoItem key={unit.email} {...fadeInRight}>
                      <ContactInfoLabel>{unit.name}</ContactInfoLabel>
                      <ContactInfoValue>
                        <ContactInfoLink href={`mailto:${unit.email}`}>
                          {unit.email}
                        </ContactInfoLink>
                      </ContactInfoValue>
                    </ContactInfoItem>
                  ))}
                </ContactInfoCard>
              )}

              <ContactInfoCard {...fadeInRight}>
                <ContactInfoTitle>{contact.contactInfo.title}</ContactInfoTitle>

                {contactInfo?.email && (
                  <ContactInfoItem {...fadeInRight}>
                    <ContactInfoLabel>{contact.contactInfo?.labels?.email || 'General Enquiries'}</ContactInfoLabel>
                    <ContactInfoValue>
                      <ContactInfoLink href={`mailto:${contactInfo.email}`}>
                        {contactInfo.email}
                      </ContactInfoLink>
                    </ContactInfoValue>
                  </ContactInfoItem>
                )}

                {(contact.contactInfo?.addressLines || footerInfo?.address) && (
                  <ContactInfoItem {...fadeInRight}>
                    <ContactInfoLabel>{contact.contactInfo?.labels?.address || 'Address'}</ContactInfoLabel>
                    <ContactInfoValue>
                      {contact.contactInfo?.addressLines
                        ? contact.contactInfo.addressLines.join(', ')
                        : footerInfo.address}
                    </ContactInfoValue>
                  </ContactInfoItem>
                )}

                {contactInfo?.website && (
                  <ContactInfoItem {...fadeInRight}>
                    <ContactInfoLabel>{contact.contactInfo?.labels?.website || 'Website'}</ContactInfoLabel>
                    <ContactInfoValue>
                      <ContactInfoLink
                        href={`https://${contactInfo.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {contactInfo.website}
                      </ContactInfoLink>
                    </ContactInfoValue>
                  </ContactInfoItem>
                )}

                {contact.contactInfo?.footerNote && (
                  <ContactInfoFooterNote {...fadeInRight}>
                    {contact.contactInfo.footerNote}
                  </ContactInfoFooterNote>
                )}
              </ContactInfoCard>
            </RightColumn>
          </ContentStack>
        </Container>
      </PageContainer>
    </>
  );
};

export default Contact;
